import React from "react";
import { InputWrapper } from "../../../../components/Input/InputWrapper";
import {
  InputLabel,
  RequiredMark,
} from "../../../../components/Input/InputLabel";
import { CustomInput } from "../../../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../../../components/Input/CustomPhoneInput";
import {
  CountryAutoComplete,
  CountryAutoCompleteItem,
} from "../../../../components/Input/CountryAutoComplete";
import Dropdown from "../../../../components/dropdown/Dropdown";
import {
  FieldGrid,
  FieldGroup,
  ReadOnlyField,
} from "../../../../components/OrgShell/FieldGroup";
import { InputError } from "../../../my-profile/InputError";
import { CustomInputChangeEvent } from "../../../../../utils/models/events/CustomInputChangeEvent";
import { OrganizationUpdateForm } from "../../models/organization-update.form";
import {
  FieldErrorMap,
  useVisibleFieldErrors,
} from "../../models/required-fields";

interface LanguageOption {
  code: string;
  label: string;
}

// Codes are data values (public/locales/{code}) — do not translate them.
export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: "en", label: "English" },
  { code: "da", label: "Danish" },
];

interface DetailsSectionProps {
  form: OrganizationUpdateForm;
  onChange: (patch: Partial<OrganizationUpdateForm>) => void;
  navNumber?: string;
  disabled?: boolean;
  isVendor?: boolean;
  /** Marks the fields required (asterisks) — customer edit screen only. */
  required?: boolean;
  /** Required-field errors by field name (customer only). */
  errors?: FieldErrorMap;
  /** Show every error (set after a blocked save attempt). */
  showErrors?: boolean;
}

export function DetailsSection({
  form,
  onChange,
  navNumber,
  disabled,
  isVendor,
  required,
  errors,
  showErrors,
}: DetailsSectionProps) {
  const { errorFor, markTouched, handleBlur } = useVisibleFieldErrors(
    errors,
    showErrors
  );

  function handleInput(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    onChange({ [target.name]: target.value });
  }

  function handleCountryChange(country: CountryAutoCompleteItem) {
    onChange({ country: country.code });
  }

  function errorClass(field: string): string | undefined {
    return errorFor(field) ? "!border-red-100" : undefined;
  }

  const selectedLanguage = LANGUAGE_OPTIONS.find(
    (x) => x.code === form.language
  );

  return (
    <div className="space-y-8">
      <FieldGroup title="Company details">
        <FieldGrid>
          <InputWrapper>
            <InputLabel>Company name{required && <RequiredMark />}</InputLabel>
            <CustomInput
              name="name"
              value={form.name}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("name")}
            />
            <InputError errorMessage={errorFor("name")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>NAV number</InputLabel>
            <ReadOnlyField value={navNumber} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Company email{required && <RequiredMark />}</InputLabel>
            <CustomInput
              type="email"
              name="email"
              value={form.email}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("email")}
            />
            <InputError errorMessage={errorFor("email")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Company phone{required && <RequiredMark />}</InputLabel>
            <CustomPhoneInput
              name="phone"
              value={form.phone}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={!!disabled}
              className={errorClass("phone")}
            />
            <InputError errorMessage={errorFor("phone")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>CVR{required && <RequiredMark />}</InputLabel>
            <CustomInput
              name="cvr"
              value={form.cvr}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("cvr")}
            />
            <InputError errorMessage={errorFor("cvr")} />
          </InputWrapper>
          <InputWrapper>
            {/* EORI is optional — no required mark, no validation. */}
            <InputLabel>EORI</InputLabel>
            <CustomInput
              name="eori"
              value={form.eori}
              onChange={handleInput}
              disabled={disabled}
            />
          </InputWrapper>
        </FieldGrid>
      </FieldGroup>

      <FieldGroup title="Address">
        <FieldGrid>
          <InputWrapper>
            <InputLabel>Address{required && <RequiredMark />}</InputLabel>
            <CustomInput
              name="address"
              value={form.address}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={disabled}
              className={errorClass("address")}
            />
            <InputError errorMessage={errorFor("address")} />
          </InputWrapper>
          <div className="grid grid-cols-2 gap-x-4">
            <InputWrapper>
              <InputLabel>Postcode{required && <RequiredMark />}</InputLabel>
              <CustomInput
                name="postCode"
                value={form.postCode}
                onChange={handleInput}
                onBlur={handleBlur}
                disabled={disabled}
                className={errorClass("postCode")}
              />
              <InputError errorMessage={errorFor("postCode")} />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>City{required && <RequiredMark />}</InputLabel>
              <CustomInput
                name="city"
                value={form.city}
                onChange={handleInput}
                onBlur={handleBlur}
                disabled={disabled}
                className={errorClass("city")}
              />
              <InputError errorMessage={errorFor("city")} />
            </InputWrapper>
          </div>
          <InputWrapper>
            <InputLabel>Country{required && <RequiredMark />}</InputLabel>
            <div data-field="country" onBlur={() => markTouched("country")}>
              <CountryAutoComplete
                disabled={!!disabled}
                selectedCode={form.country}
                onCountryChange={handleCountryChange}
                error={!!errorFor("country")}
              />
            </div>
            <InputError errorMessage={errorFor("country")} />
          </InputWrapper>
        </FieldGrid>
      </FieldGroup>

      {!isVendor && (
        <>
          <FieldGroup
            title="Language"
            description="Language used for the customer's interface and emails."
          >
            <div className="w-64">
              <Dropdown
                selectedOption={selectedLanguage}
                options={LANGUAGE_OPTIONS}
                defaultTitle="Select language"
                getOptionDisplay={(option) => option.label}
                onOptionSelect={(option) => {
                  if (!option) return;
                  onChange({ language: option.code });
                }}
              />
            </div>
          </FieldGroup>

          <FieldGroup title="Status">
            <label className="flex cursor-pointer items-center space-x-2">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.disabled ?? false}
                onChange={(e) => onChange({ disabled: e.target.checked })}
              />
              <span className="text-sm font-bold text-red">
                Deactivate this customer
              </span>
            </label>
            <p className="ml-6 text-xs text-gray-500">
              A deactivated customer can no longer sign in or use the webshop.
              You can reactivate them at any time.
            </p>
          </FieldGroup>
        </>
      )}
    </div>
  );
}
